import React, { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import DatePicker from './DatePicker';
import ConfirmationDialog from './ConfirmationDialog';
import LocationSelectionModal from './LocationSelectionModal';
import { updateBuyerInfo, checkSerialNumber, getRecordFiles } from '../services/api';
import { getCities, getDistricts } from '../services/sparePartsService';
import { FileService } from '../services/fileService';
import { ActionType, MediaFile } from '../types/ServiceRequest';
import { API_BASE_URL } from '../constants/api';
import { useUser } from '../contexts/UserContext';
import { formatPhoneNumber, cleanPhoneNumber, isValidTurkishMobile, handlePhoneKeyDown } from '../utils/phoneUtils';
import { normalizeEmail, isValidEmail } from '../utils/emailUtils';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';

// Koddan elle değiştirilir. true iken Fatura No ve Belge No gizlenir ve zorunlu olmaz.
const hideFaturaNoAndBelgeNo = true;

interface City {
  Id: number;
  Ad: string;
}

interface District {
  Id: number;
  Ad: string;
}

// ServiceRequest'teki MediaFileWithStatus ile uyumlu interface
interface MediaFileWithStatus extends MediaFile {
  uploadStatus?: 'uploading' | 'success' | 'error';
  existingFileId?: number;
  existingFileRumuz?: string;
  streamUrl?: string; // Video stream için ayrı URL
  // Dosya bilgileri için ek alanlar
  existingFileName?: string;
  existingFileType?: string;
  existingFileSize?: number;
  existingFileDbId?: number;
}

interface BuyerInfo {
  faturaNo: string;
  belgeNo: string;
  faturaTarihi: string;
  teslimTarihi: string;
  teslimYeri: string;
  telefon: string;
  teslimEdilen: string;
  email: string;
  adres: string;
  il?: number;
  ilce?: number;
  imageFile?: MediaFileWithStatus;
}

interface BuyerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (buyerInfo: BuyerInfo) => void;
  onError: (error: string) => void;
  onAlreadyExists: (updatedBuyerInfo: BuyerInfo, sonIslem: number) => void;
  productId: string;
  serialNumber: string;
  initialBuyerInfo?: BuyerInfo;
  productData?: any; // ProductData from ServiceRequest context
}

export default function BuyerInfoModal({ isOpen, onClose, onSuccess, onError, onAlreadyExists, productId, serialNumber, initialBuyerInfo, productData }: BuyerInfoModalProps) {
  const { userData } = useUser();

  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo>({
    faturaNo: '',
    belgeNo: '',
    faturaTarihi: '',
    teslimTarihi: '',
    teslimYeri: '',
    telefon: '',
    teslimEdilen: '',
    email: '',
    adres: '',
    il: undefined,
    ilce: undefined,
    imageFile: undefined
  });

  // Medya dosyaları için refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Drag & Drop state'leri
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const dragImageCounterRef = useRef(0);

  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [loading, setLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletedFiles, setDeletedFiles] = useState<MediaFileWithStatus[]>([]);
  const [isImagePreviewLoading, setIsImagePreviewLoading] = useState(false);

  // İl ve İlçe states
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Modal states
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isDistrictModalOpen, setIsDistrictModalOpen] = useState(false);

  // Modal geri düğmesi handler - alt modal'lar açıkken devre dışı
  const isModalBackHandlerEnabled = !isConfirmationOpen && !isCityModalOpen && !isDistrictModalOpen;
  
  // onClose'u useCallback ile sarmalayalım
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: isModalBackHandlerEnabled
  });

  // Search states
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');

  // Medya dosyaları için yardımcı fonksiyonlar
  const imageFile = buyerInfo.imageFile;
  const hasImage = !!imageFile;
  const canAddImage = !hasImage;

  // Dosya benzersizliği kontrol fonksiyonu - tek dosya için gereksiz, kaldırıldı

  // API functions
  const fetchExistingImageFile = useCallback(async (productId: string) => {
    try {
      const filesResponse = await getRecordFiles(productId, 'Kuponlar');
      if (filesResponse.success && filesResponse.data && filesResponse.data.length > 0) {
        // Sadece ilk görseli bul
        const imageFile = filesResponse.data.find((file: any) => {
          const isVideo = file.Tip && (
            file.Tip.toLowerCase().includes('video') || 
            file.Ad.toLowerCase().endsWith('.mp4') ||
            file.Ad.toLowerCase().endsWith('.avi') ||
            file.Ad.toLowerCase().endsWith('.mov') ||
            file.Ad.toLowerCase().endsWith('.wmv')
          );
          return !isVideo; // Video olmayan ilk dosya
        });

        if (imageFile) {
          try {
            // Dosya URL'ini oluştur
            const posterUrl = `${API_BASE_URL}/files/download/${imageFile.Rumuz}`;
            
            // Fake File object oluştur
            const fakeFile = new File([''], imageFile.Ad, { 
              type: imageFile.Tip || 'image/jpeg' 
            });
            
            const mediaFile: MediaFileWithStatus = {
              id: `existing-${imageFile.Id}`,
              file: fakeFile,
              preview: posterUrl,
              type: 'image',
              uploading: false,
              existingFileId: imageFile.Id,
              existingFileRumuz: imageFile.Rumuz,
              existingFileName: imageFile.Ad,
              existingFileType: imageFile.Tip,
              existingFileSize: imageFile.Boyut,
              existingFileDbId: imageFile.Id
            };

            setIsImagePreviewLoading(true);

            setBuyerInfo(prev => ({
              ...prev,
              imageFile: mediaFile
            }));
          } catch (fileError) {
            console.error('Dosya işlenirken hata:', fileError);
          }
        }
      }
    } catch (error) {
      console.error('Mevcut görsel yüklenirken hata:', error);
    }
  }, []);

  const fetchCities = useCallback(async (countryId: number) => {
    setLoadingCities(true);
    try {
      const data = await getCities(countryId);
      const cityList = data.data || [];
      setCities(cityList);
    } catch (error) {
      console.error('İl listesi yüklenirken hata:', error);
      onError('İl listesi yüklenirken bir hata oluştu.');
    } finally {
      setLoadingCities(false);
    }
  }, [onError]);

  const fetchDistricts = useCallback(async (cityId: number) => {
    setLoadingDistricts(true);
    try {
      const data = await getDistricts(cityId);
      setDistricts(data.data || []);
    } catch (error) {
      console.error('İlçe listesi yüklenirken hata:', error);
      onError('İlçe listesi yüklenirken bir hata oluştu.');
    } finally {
      setLoadingDistricts(false);
    }
  }, [onError]);

  // Tek görsel yükleme fonksiyonu
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const resetInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    // Zaten görsel varsa hata ver
    if (hasImage) {
      onError('Zaten bir görsel yüklenmiş. Önce mevcut görseli silin.');
      resetInput();
      return;
    }

    const file = e.target.files[0];

    if (!file.type.startsWith('image/')) {
      onError('Sadece görsel dosyaları yükleyebilirsiniz.');
      resetInput();
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      onError('Görsel dosyaları en fazla 25 MB olabilir.');
      resetInput();
      return;
    }

    const preview = URL.createObjectURL(file);

    const newImageFile: MediaFileWithStatus = {
      id: uuidv4(),
      file,
      preview,
      type: 'image',
      uploadStatus: undefined
    };

    setBuyerInfo(prev => ({
      ...prev,
      imageFile: newImageFile
    }));
    
    resetInput();
  };

  const handleRemoveImage = () => {
    if (!imageFile) return;

    setIsImagePreviewLoading(false);

    // Eğer silinen dosya sunucuda kayıtlıysa, silinecek dosyalar listesine ekle
    if (imageFile.existingFileId && imageFile.existingFileRumuz) {
      setDeletedFiles(prev => [...prev, imageFile]);
    }

    // Yeni eklenen dosyalar için URL'i temizle
    if (!imageFile.existingFileId) {
      URL.revokeObjectURL(imageFile.preview);
    }

    setBuyerInfo(prev => ({
      ...prev,
      imageFile: undefined
    }));
  };

  // Drag & Drop handler'ları - tek görsel için sadeleştirildi
  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddImage && !loading && !isUploading) {
      setIsDraggingImage(true);
    }
  };

  const handleImageDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddImage && !loading && !isUploading) {
      dragImageCounterRef.current++;
      setIsDraggingImage(true);
    }
  };

  const handleImageDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (canAddImage && !loading && !isUploading) {
      dragImageCounterRef.current--;
      if (dragImageCounterRef.current <= 0) {
        dragImageCounterRef.current = 0;
        setIsDraggingImage(false);
      }
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragImageCounterRef.current = 0;
    setIsDraggingImage(false);

    if (!canAddImage || loading || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      onError('Sadece görsel dosyaları yükleyebilirsiniz.');
      return;
    }

    const file = imageFiles[0]; // Sadece ilk dosyayı al

    if (file.size > 25 * 1024 * 1024) {
      onError('Görsel dosyaları en fazla 25 MB olabilir.');
      return;
    }

    const preview = URL.createObjectURL(file);

    const newImageFile: MediaFileWithStatus = {
      id: uuidv4(),
      file,
      preview,
      type: 'image',
      uploadStatus: undefined
    };

    setBuyerInfo(prev => ({
      ...prev,
      imageFile: newImageFile
    }));
  };

  // Silinen dosyaları sunucudan silme fonksiyonu
  const deleteRemovedFiles = async () => {
    if (deletedFiles.length === 0) return;

    try {
      // Silinen dosyaları tek tek sunucudan sil
      for (const deletedFile of deletedFiles) {
        try {
          // Önce /files/destroy/Kuponlar endpoint'ini çağır
          await FileService.deleteFile(deletedFile.existingFileRumuz!, 'Kuponlar');
          console.log(`Dosya sunucudan silindi: ${deletedFile.existingFileRumuz}`);
          
          // Sonra /data/destroy endpoint'ini çağır
          await deleteFileFromData(deletedFile);
          console.log(`Dosya veri tabanından silindi: ${deletedFile.existingFileRumuz}`);
        } catch (deleteError) {
          console.error(`Dosya silme hatası (${deletedFile.existingFileRumuz}):`, deleteError);
          // Tek dosya silme hatası tüm işlemi durdurmasın
        }
      }
    } catch (error) {
      console.error('Silinen dosyalar sunucudan silinirken hata:', error);
    }
  };

  // /data/destroy endpoint'ini çağıran fonksiyon
  const deleteFileFromData = async (deletedFile: MediaFileWithStatus) => {
    try {
      const masterId = parseInt(productId);
      if (isNaN(masterId)) {
        throw new Error('Geçersiz productId');
      }

      // Form data oluştur
      const formData = new URLSearchParams();
      formData.append('isTree', 'false');
      formData.append('limitless', 'false');
      formData.append('storeId', `store-${Date.now()}`);
      formData.append('model', 'Dosyalar');
      formData.append('fields', 'Model,Kayit,Rumuz,Ad,Tip,Boyut,Id');
      formData.append('writemodel', '');
      formData.append('fromview', 'false');
      formData.append('masterfield', '');
      formData.append('masterid', '0');
      
      // Prefilter JSON'u oluştur
      const prefilter = JSON.stringify([
        { type: "string", field: "Model", value: "Kuponlar" },
        { type: "numeric", field: "Kayit", value: masterId }
      ]);
      formData.append('prefilter', prefilter);
      
      // Data JSON'u oluştur (gerçek dosya bilgileri)
      const data = JSON.stringify({
        Model: "Kuponlar",
        Kayit: masterId,
        Rumuz: deletedFile.existingFileRumuz,
        Ad: deletedFile.existingFileName,
        Tip: deletedFile.existingFileType,
        Boyut: deletedFile.existingFileSize,
        Id: deletedFile.existingFileDbId,
        Tracker: ""
      });
      formData.append('data', data);

      // API çağrısı yap
      const response = await fetch(`${API_BASE_URL}/data/destroy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include', // Session cookie'leri dahil et
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.text();
      console.log('Data destroy response:', result);
      
    } catch (error) {
      console.error('Data destroy hatası:', error);
      throw error;
    }
  };

  // Tek görseli upload etme fonksiyonu
  const uploadImageFile = async (masterId: number) => {
    if (!imageFile) return;

    // Sadece yeni eklenen dosyaları upload et (existingFileId olmayan)
    if (imageFile.existingFileId) return; // Mevcut dosya, upload etme

    setIsUploading(true);

    try {
      // Upload durumunu 'uploading' yap
      setBuyerInfo(prev => ({
        ...prev,
        imageFile: prev.imageFile ? { ...prev.imageFile, uploadStatus: 'uploading' } : undefined
      }));
        
      // Dosyayı yükle - Model değerini 'Kuponlar' olarak ayarla
      await FileService.uploadFile(
        imageFile.file,
        'Kuponlar', 
        masterId,
        true // thumb
      );
      
      // Başarılı yükleme
      setBuyerInfo(prev => ({
        ...prev,
        imageFile: prev.imageFile ? { ...prev.imageFile, uploadStatus: 'success' } : undefined
      }));
      
    } catch (uploadError) {
      console.error('Dosya yükleme hatası:', uploadError);
      
      // Hatalı yükleme
      setBuyerInfo(prev => ({
        ...prev,
        imageFile: prev.imageFile ? { ...prev.imageFile, uploadStatus: 'error' } : undefined
      }));
    } finally {
      setIsUploading(false);
    }
  };

  // Global drag & drop olaylarını engelle
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('drop', handleGlobalDrop);

    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  // Modal açıldığında mevcut alıcı bilgilerini yükle (sadece modal ilk açıldığında)
  useEffect(() => {
    if (isOpen) {
      if (initialBuyerInfo) {
      setBuyerInfo({
        ...initialBuyerInfo,
        telefon: formatPhoneNumber(initialBuyerInfo.telefon || ''),
        email: normalizeEmail(initialBuyerInfo.email || ''),
        imageFile: initialBuyerInfo.imageFile
      });

        // Mevcut il bilgisi varsa il ve ilçeleri yükle
        if (initialBuyerInfo.il && productData?.Ulke) {
          fetchCities(productData.Ulke).then(() => {
            const existingCity = cities.find(city => city.Id === initialBuyerInfo.il);
            if (existingCity) {
              setSelectedCity(existingCity);
              if (initialBuyerInfo.ilce) {
                fetchDistricts(initialBuyerInfo.il!).then(() => {
                  const existingDistrict = districts.find(district => district.Id === initialBuyerInfo.ilce);
                  if (existingDistrict) {
                    setSelectedDistrict(existingDistrict);
                  }
                });
              }
            }
          });
        }
      } else {
        // İlk açılışta initialBuyerInfo yoksa formu temizle
      setBuyerInfo({
        faturaNo: '',
        belgeNo: '',
        faturaTarihi: '',
        teslimTarihi: '',
        teslimYeri: '',
        telefon: '',
        teslimEdilen: '',
        email: '',
          adres: '',
          il: undefined,
          ilce: undefined,
          imageFile: undefined
        });
      }
    }
  }, [isOpen]); // initialBuyerInfo bağımlılığını kaldırdık

  // Modal açıldığında ülke bilgisine göre illeri yükle
  useEffect(() => {
    if (isOpen && productData?.Ulke) {
      fetchCities(productData.Ulke);
    }
  }, [isOpen, productData?.Ulke, fetchCities]);

  // Modal açıldığında mevcut görseli yükle
  useEffect(() => {
    if (isOpen && productId) {
      fetchExistingImageFile(productId);
    }
  }, [isOpen, productId, fetchExistingImageFile]);

  // İller yüklendiğinde SaticiIl'i seç
  useEffect(() => {
    if (!isOpen) return;

    if (cities.length > 0 && productData?.SaticiIl && !selectedCity) {
      const defaultCity = cities.find(city => city.Id === productData.SaticiIl);
      if (defaultCity) {
        setSelectedCity(defaultCity);
        setBuyerInfo(prev => ({ ...prev, il: defaultCity.Id }));
        fetchDistricts(defaultCity.Id);
      }
    }
  }, [isOpen, cities, productData?.SaticiIl, fetchDistricts, selectedCity]);

  // İlçeler yüklendiğinde SaticiIlce'yi seç
  useEffect(() => {
    if (!isOpen) return;

    if (districts.length > 0 && productData?.SaticiIlce && !selectedDistrict) {
      const defaultDistrict = districts.find(district => district.Id === productData.SaticiIlce);
      if (defaultDistrict) {
        setSelectedDistrict(defaultDistrict);
        setBuyerInfo(prev => ({ ...prev, ilce: defaultDistrict.Id }));
      }
    }
  }, [isOpen, districts, productData?.SaticiIlce, selectedDistrict]);

  const validateForm = () => {
    // Mevcut error state'ini koru ve sadece gerekli güncellemeleri yap
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };

    // Fatura tarihi sadece BuyerInvoiceInfoRequired true ise zorunlu.
    // Fatura No ve Belge No, hideFaturaNoAndBelgeNo true iken kesinlikle zorunlu olmaz.
    if (userData?.BuyerInvoiceInfoRequired && !hideFaturaNoAndBelgeNo) {
        newErrors.faturaNo = !buyerInfo.faturaNo.trim() ? 'error' : undefined;
        newErrors.belgeNo = !buyerInfo.belgeNo.trim() ? 'error' : undefined;
      } else {
        newErrors.faturaNo = undefined;
        newErrors.belgeNo = undefined;
      }

      if (userData?.BuyerInvoiceInfoRequired) {
        newErrors.faturaTarihi = !buyerInfo.faturaTarihi.trim() ? 'error' : undefined;
      } else {
        newErrors.faturaTarihi = undefined;
      }

    // Bu alanlar her zaman zorunlu
      newErrors.teslimTarihi = !buyerInfo.teslimTarihi.trim() ? 'error' : undefined;
      newErrors.teslimYeri = !buyerInfo.teslimYeri.trim() ? 'error' : undefined;
      newErrors.telefon = !buyerInfo.telefon.trim() ? 'error' : undefined;
      newErrors.teslimEdilen = !buyerInfo.teslimEdilen.trim() ? 'error' : undefined;
      newErrors.il = !buyerInfo.il ? 'error' : undefined;
      newErrors.ilce = !buyerInfo.ilce ? 'error' : undefined;

    // E-posta format kontrolü (zorunlu değil ama girilmişse geçerli olmalı)
    if (buyerInfo.email.trim() && !isValidEmail(buyerInfo.email)) {
      newErrors.email = 'error';
      } else {
        newErrors.email = undefined;
    }

    // Telefon format kontrolü
      if (buyerInfo.telefon.trim() && !isValidTurkishMobile(buyerInfo.telefon)) {
        newErrors.telefon = 'error';
      }

      return newErrors;
    });

    // Hata kontrolü için yeni bir validation yap
    let hasErrors = false;

    // Fatura bilgileri kontrolü
    if (userData?.BuyerInvoiceInfoRequired) {
      if (!hideFaturaNoAndBelgeNo && (!buyerInfo.faturaNo.trim() || !buyerInfo.belgeNo.trim())) {
        hasErrors = true;
      }
      if (!buyerInfo.faturaTarihi.trim()) {
        hasErrors = true;
      }
    }

    // Zorunlu alanlar kontrolü
    if (!buyerInfo.teslimTarihi.trim() || !buyerInfo.teslimYeri.trim() ||
      !buyerInfo.telefon.trim() || !buyerInfo.teslimEdilen.trim() ||
      !buyerInfo.il || !buyerInfo.ilce) {
      hasErrors = true;
    }

    // E-posta format kontrolü
    if (buyerInfo.email.trim() && !isValidEmail(buyerInfo.email)) {
      hasErrors = true;
    }

    // Telefon format kontrolü
    if (buyerInfo.telefon.trim() && !isValidTurkishMobile(buyerInfo.telefon)) {
      hasErrors = true;
    }

    // Hata varsa callback ile bildir
    if (hasErrors) {
      onError('Lütfen tüm zorunlu alanları doldurun.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon kontrolü yap
    const isValid = validateForm();

    // Sadece validasyon başarılıysa onay dialogunu aç
    if (isValid) {
    setIsConfirmationOpen(true);
    }
  };

  const handleConfirmSave = async () => {
    setLoading(true);

    try {
      // Önce seri numarası kontrolü yap
      const serialCheckResult = await checkSerialNumber(serialNumber);

      if (serialCheckResult.success && serialCheckResult.data) {
        // Durumu kontrol et
        if (!serialCheckResult.isWaiting) {
          // SonIslem CREATED veya WAITING_FOR_APPROVAL değilse uyarı ver
          const apiData = serialCheckResult.data;

          // Tarih formatını dönüştürme fonksiyonu
          const formatDateFromApi = (dateString: string) => {
            if (!dateString) return '';
            const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
            if (isNaN(timestamp)) return '';
            const date = new Date(timestamp);
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
          };

          const updatedBuyerInfo = {
            faturaNo: apiData.AliciFaturaNo || '',
            belgeNo: apiData.AliciBelgeNo || '',
            faturaTarihi: formatDateFromApi(apiData.AliciFaturaTarihi) || '',
            teslimTarihi: formatDateFromApi(apiData.AliciTeslimTarihi) || '',
            teslimYeri: apiData.AliciTeslimYeri || '',
            telefon: apiData.AliciTelefon || '',
            teslimEdilen: apiData.AliciTeslimEdilen || '',
            email: apiData.AliciEPosta || '',
            adres: apiData.AliciAdres || '',
            imageFile: buyerInfo.imageFile // Mevcut görseli koru
          };

          // Zaten mevcut durumunu ana component'e bildir
          onAlreadyExists(updatedBuyerInfo, apiData.SonIslem);
          handleClose();
          return;
        }
      } else {
        // Seri numarası bulunamadı
        onError(serialCheckResult.error || 'Seri numarası kontrol edilemedi');
        return;
      }

      // SonIslem CREATED veya WAITING_FOR_APPROVAL ise normal kaydet işlemine devam et
      // Görseli de dahil ederek buyerInfo'yu gönder
      const buyerInfoWithImage = {
        ...buyerInfo,
        imageFile: buyerInfo.imageFile
      };
      
      const result = await updateBuyerInfo(buyerInfoWithImage, productId);

      if (result.success) {
        // Başarılı kayıt sonrası görseli upload et
        if (buyerInfoWithImage.imageFile) {
          // productId'yi number'a çevir (masterId olarak kullanılacak)
          const masterId = parseInt(productId);
          if (!isNaN(masterId)) {
            await uploadImageFile(masterId);
          }
        }
        
        // Silinen dosyaları sunucudan sil
        await deleteRemovedFiles();
        
        // Başarılı kayıt
        onSuccess(buyerInfoWithImage);
        handleClose();
      } else {
        // Hata durumu
        onError(result.error || 'Kayıt işlemi başarısız');
      }
    } catch (error) {
      console.error('Alıcı bilgileri kaydedilirken hata:', error);
      onError('Kayıt işlemi sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Görselin URL'ini temizle
    if (buyerInfo.imageFile && !buyerInfo.imageFile.existingFileId) {
      URL.revokeObjectURL(buyerInfo.imageFile.preview);
    }

    setBuyerInfo({
      faturaNo: '',
      belgeNo: '',
      faturaTarihi: '',
      teslimTarihi: '',
      teslimYeri: '',
      telefon: '',
      teslimEdilen: '',
      email: '',
      adres: '',
      il: undefined,
      ilce: undefined,
      imageFile: undefined
    });
    setErrors({});
    setLoading(false);
    setIsConfirmationOpen(false);
    setCitySearchTerm('');
    setDistrictSearchTerm('');
    setIsCityModalOpen(false);
    setIsDistrictModalOpen(false);
    setIsDraggingImage(false);
    dragImageCounterRef.current = 0;
    setDeletedFiles([]);
    setIsImagePreviewLoading(false);
    onClose();
  };

  // Modal tamamen kapandığında state'leri temizle
  useEffect(() => {
    if (!isOpen) {
      setSelectedCity(null);
      setSelectedDistrict(null);
      setCities([]);
      setDistricts([]);
    }
  }, [isOpen]);

  // ProductData değiştiğinde seçimleri sıfırla (farklı ürün için modal açılıyor)
  useEffect(() => {
    setSelectedCity(null);
    setSelectedDistrict(null);
    setCities([]);
    setDistricts([]);
  }, [productId]);





  const handleInputChange = (field: keyof BuyerInfo, value: string | number | undefined) => {
    setBuyerInfo(prev => ({
      ...prev,
      [field]: value
    }));

    // Kullanıcı yazmaya başladığında sadece o alanın hatasını temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Telefon numarası için özel handler
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange('telefon', formatted);
  };

  // E-posta için özel handler
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeEmail(e.target.value);
    handleInputChange('email', normalized);
  };

  // Telefon alanında backspace işlemini yönet - phoneUtils'den kullanıyoruz
  const phoneKeyDownHandler = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Özel bir setPhoneNumber fonksiyonu oluşturuyoruz
    const setPhoneNumber = (value: string) => handleInputChange('telefon', value);

    // phoneError state'i için errors.telefon kontrolü
    const phoneErrorState = !!errors.telefon;
    const setPhoneErrorState = (value: boolean) => {
      if (!value) {
        setErrors(prev => ({ ...prev, telefon: undefined }));
      }
    };

    handlePhoneKeyDown(e, setPhoneNumber, !!Object.keys(errors).length, phoneErrorState, setPhoneErrorState);
  }, [errors, handleInputChange]);

  // İl ve İlçe handler'ları
  const handleCitySelect = (city: City | null) => {
    if (city) {
      setSelectedCity(city);
      setSelectedDistrict(null); // İl değiştiğinde ilçeyi sıfırla
      setBuyerInfo(prev => ({ ...prev, il: city.Id, ilce: undefined }));
      setDistricts([]); // Mevcut ilçe listesini temizle
      fetchDistricts(city.Id);
      setIsCityModalOpen(false);
      setCitySearchTerm('');

      // Hata varsa temizle
      if (errors.il) {
        setErrors(prev => ({ ...prev, il: undefined }));
      }
    }
  };

  const handleDistrictSelect = (district: District | null) => {
    if (district) {
      setSelectedDistrict(district);
      setBuyerInfo(prev => ({ ...prev, ilce: district.Id }));
      setIsDistrictModalOpen(false);
      setDistrictSearchTerm('');

      // Hata varsa temizle
      if (errors.ilce) {
        setErrors(prev => ({ ...prev, ilce: undefined }));
      }
    }
  };

  // Arama fonksiyonları
  const handleCitySearchChange = (term: string) => {
    setCitySearchTerm(term);
  };

  const handleDistrictSearchChange = (term: string) => {
    setDistrictSearchTerm(term);
  };

  // Filtrelenmiş listeler


  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          >
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative transform transition-transform overflow-visible rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-2xl"
                role="dialog"
                aria-modal="true"
              >
                {/* Header */}
                <div className="border-b border-gray-300 dark:border-gray-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Müşteri Bilgileri
                    </h3>
                    <AnimatedCloseButton 
                      onClick={handleClose}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 pt-4 pb-5 sm:p-8 overflow-visible modal-scrollable">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
                    {/* Fatura bilgileri - sadece BuyerInvoiceInfoRequired true ise göster */}
                    {userData?.BuyerInvoiceInfoRequired && (
                      <>
                        {!hideFaturaNoAndBelgeNo && (
                          <>
                        {/* Fatura No */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fatura No <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={buyerInfo.faturaNo}
                            onChange={(e) => handleInputChange('faturaNo', e.target.value)}
                            maxLength={20}
                            className={errors.faturaNo ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                            placeholder="Fatura numarasını girin"
                          />
                        </div>

                        {/* Belge No */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Belge No <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={buyerInfo.belgeNo}
                            onChange={(e) => handleInputChange('belgeNo', e.target.value)}
                            maxLength={20}
                            className={errors.belgeNo ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                            placeholder="Belge numarasını girin"
                          />
                        </div>
                          </>
                        )}

                        {/* Fatura Tarihi */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fatura Tarihi <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            value={buyerInfo.faturaTarihi}
                            onChange={(date) => handleInputChange('faturaTarihi', date)}
                            placeholder="Fatura tarihi seçin"
                            hasError={!!errors.faturaTarihi}
                          />
                        </div>
                      </>
                    )}

                    {/* Teslim Edilen */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Teslim Edilen <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={buyerInfo.teslimEdilen}
                        onChange={(e) => handleInputChange('teslimEdilen', e.target.value)}
                        maxLength={200}
                        className={errors.teslimEdilen ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Teslim edilen kişi adı"
                      />
                    </div>

                    {/* Teslim Yeri */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Teslim Yeri <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={buyerInfo.teslimYeri}
                        onChange={(e) => handleInputChange('teslimYeri', e.target.value)}
                        maxLength={200}
                        className={errors.teslimYeri ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Teslim yeri bilgisini girin"
                      />
                    </div>

                    {/* Teslim Tarihi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Teslim Tarihi <span className="text-red-500">*</span>
                      </label>
                      <DatePicker
                        value={buyerInfo.teslimTarihi}
                        onChange={(date) => handleInputChange('teslimTarihi', date)}
                        placeholder="Teslim tarihi seçin"
                        hasError={!!errors.teslimTarihi}
                      />
                    </div>                    

                    {/* Telefon - Fatura bilgileri gizliyse burada göster */}
                    {!userData?.BuyerInvoiceInfoRequired && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Telefon <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="phone-input"
                          type="tel"
                          value={buyerInfo.telefon}
                          onChange={handlePhoneChange}
                          onKeyDown={phoneKeyDownHandler}
                          maxLength={17}
                          className={errors.telefon ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                          placeholder="Telefon numarasını girin"
                        />
                      </div>
                    )}

                    {/* İl Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Alıcı İl <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div
                          onClick={() => productData?.Ulke && setIsCityModalOpen(true)}
                          tabIndex={productData?.Ulke ? 0 : -1}
                          className={`${errors.il ? 'form-combobox-error' : 'form-combobox'} w-full relative ${productData?.Ulke ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                            }`}
                        >
                          <span className={selectedCity ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                            {selectedCity ? selectedCity.Ad : (!initialBuyerInfo && productData?.SaticiIlAdi ? productData.SaticiIlAdi : 'İl seçin')}
                          </span>
                          <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCityModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* İlçe Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Alıcı İlçe <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div
                          onClick={() => selectedCity && setIsDistrictModalOpen(true)}
                          tabIndex={selectedCity ? 0 : -1}
                          className={`${errors.ilce ? 'form-combobox-error' : 'form-combobox'} w-full relative ${selectedCity ? 'cursor-pointer bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 hover:border-gray-400 dark:hover:border-gray-500' : 'cursor-not-allowed opacity-50 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2'
                            }`}
                        >
                          <span className={selectedDistrict ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                            {selectedDistrict ? selectedDistrict.Ad : (!initialBuyerInfo && productData?.SaticiIlceAdi ? productData.SaticiIlceAdi : 'İlçe seçin')}
                          </span>
                          <svg className={`h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isDistrictModalOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Telefon - Fatura bilgileri varsa burada göster */}
                    {userData?.BuyerInvoiceInfoRequired && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="phone-input"
                        type="tel"
                        value={buyerInfo.telefon}
                        onChange={handlePhoneChange}
                          onKeyDown={phoneKeyDownHandler}
                        maxLength={17}
                          className={errors.telefon ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="Telefon numarasını girin"
                      />
                    </div>
                    )}

                    {/* E-Posta - Fatura bilgileri varsa Telefon ile aynı satırda */}
                    {userData?.BuyerInvoiceInfoRequired && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        E-Posta
                      </label>
                      <input
                        type="email"
                        value={buyerInfo.email}
                        onChange={handleEmailChange}
                        maxLength={100}
                          className={errors.email ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="E-posta adresi (isteğe bağlı)"
                      />
                    </div>
                    )}

                  </div>

                  {/* E-Posta - Fatura bilgileri yoksa full width */}
                  {!userData?.BuyerInvoiceInfoRequired && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        E-Posta
                      </label>
                      <input
                        type="email"
                        value={buyerInfo.email}
                        onChange={handleEmailChange}
                        maxLength={100}
                        className={errors.email ? 'form-input-error w-full' : 'form-input w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                        placeholder="E-posta adresi (isteğe bağlı)"
                      />
                    </div>
                  )}

                  {/* Adres - Full width */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Adres
                    </label>
                    <textarea
                      value={buyerInfo.adres}
                      onChange={(e) => handleInputChange('adres', e.target.value)}
                      rows={3}
                      maxLength={200}
                      className={errors.adres ? 'form-textarea-error w-full' : 'form-textarea w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                      placeholder="Adres bilgisi (isteğe bağlı)"
                    />
                  </div>

                   {/* Medya Dosyaları Bölümü */}
                   <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 overflow-hidden shadow-sm">
                      <div className="rounded-lg border border-white dark:border-gray-700 overflow-hidden">
                        {/* Görsel Bölümü */}
                        <div className="p-4">

                          <div className="mb-3">
                            {hasImage ? (
                              <div className="relative aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden group">
                                <img
                                  src={imageFile.preview}
                                  alt="Yüklenen görsel"
                                  className={`w-full h-full object-contain p-5 transition-opacity duration-300 ${isImagePreviewLoading ? 'opacity-0' : 'opacity-100'}`}
                                  onLoad={() => setIsImagePreviewLoading(false)}
                                  onError={() => setIsImagePreviewLoading(false)}
                                />

                                {isImagePreviewLoading && (
                                  <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 flex items-center justify-center z-10">
                                    <svg className="animate-spin w-6 h-6 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </div>
                                )}
                                
                                {/* Upload durumu overlay */}
                                {imageFile.uploadStatus === 'uploading' && (
                                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                                    <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </div>
                                )}
                                
                                {imageFile.uploadStatus === 'error' && (
                                  <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center z-20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </div>
                                )}
                                
                                {imageFile.uploadStatus === 'success' && (
                                  <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center z-20 opacity-0 animate-pulse">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                )}
                                
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveImage();
                                  }}
                                  disabled={loading || isUploading}
                                  className={`absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 z-30 shadow-md transition-opacity duration-200 ${
                                    loading || isUploading
                                      ? 'opacity-50 cursor-not-allowed'
                                      : 'md:opacity-0 group-hover:opacity-100'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-25"></div>
                              </div>
                            ) : (
                              <label 
                                className={`aspect-square flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 group ${
                                  isDraggingImage 
                                    ? 'border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 scale-105' 
                                    : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-blue-50 hover:via-indigo-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:via-indigo-900/15 dark:hover:to-blue-800/20 hover:border-blue-500 dark:hover:border-blue-400'
                                } ${loading || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onDragOver={handleImageDragOver}
                                onDragEnter={handleImageDragEnter}
                                onDragLeave={handleImageDragLeave}
                                onDrop={handleImageDrop}
                              >
                                <svg className={`w-12 h-12 mb-3 transition-colors duration-200 ${
                                  isDraggingImage 
                                    ? 'text-blue-500 dark:text-blue-400' 
                                    : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400'
                                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                </svg>
                                <span className={`text-base mb-1 transition-colors duration-200 ${
                                  isDraggingImage 
                                    ? 'text-blue-700 dark:text-blue-300 font-medium' 
                                    : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-700 dark:group-hover:text-blue-300'
                                }`}>
                                  {isDraggingImage ? 'Görsel Bırakın' : 'Görsel Ekle'}
                                </span>
                                {!isDraggingImage && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400">veya buraya sürükleyin</span>
                                )}
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                />
                              </label>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            En fazla 1 görsel yükleyebilirsiniz. Desteklenen formatlar: JPG, PNG, GIF. Maksimum dosya boyutu: 25 MB.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>

                {/* Footer */}
                <div className="border-t border-gray-300 dark:border-gray-600 px-6 py-4">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading || isUploading}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      <div className="rounded-lg border border-white dark:border-gray-700 px-5 py-2 text-base font-medium text-gray-700 dark:text-gray-300">
                        İptal
                      </div>
                    </button>
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={loading || isUploading}
                      className="px-5 py-2 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                    >
                      {(loading || isUploading) && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {isUploading ? 'Dosyalar Yükleniyor...' : 'Kaydet'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Onay Dialogu */}
      <ConfirmationDialog
        isOpen={isConfirmationOpen}
        title="Müşteri Bilgileri Kaydet"
        message="Girdiğiniz müşteri bilgileri ürün kaydına eklenecektir. Bilgilerin doğruluğundan emin misiniz?"
        autoClose={true}
        onCancel={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirmSave}
      />

      {/* İl Seçim Modal'ı */}
      <LocationSelectionModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        onSelect={handleCitySelect}
        items={cities}
        selectedId={selectedCity?.Id}
        highlightId={!initialBuyerInfo ? productData?.SaticiIl : undefined}
        loading={loadingCities}
        title="İl Seçin"
        searchPlaceholder="İl ara..."
        emptyMessage={productData?.Ulke ? 'Uygun il bulunamadı.' : 'Önce ülke seçin.'}
        searchTerm={citySearchTerm}
        onSearchChange={handleCitySearchChange}
        isRequired={true}
        formSubmitted={!!errors.il}
        error={!!errors.il}
      />

      {/* İlçe Seçim Modal'ı */}
      <LocationSelectionModal
        isOpen={isDistrictModalOpen}
        onClose={() => setIsDistrictModalOpen(false)}
        onSelect={handleDistrictSelect}
        items={districts}
        selectedId={selectedDistrict?.Id}
        highlightId={!initialBuyerInfo ? productData?.SaticiIlce : undefined}
        loading={loadingDistricts}
        title="İlçe Seçin"
        searchPlaceholder="İlçe ara..."
        emptyMessage={selectedCity ? 'Uygun ilçe bulunamadı.' : 'Önce il seçin.'}
        searchTerm={districtSearchTerm}
        onSearchChange={handleDistrictSearchChange}
        isRequired={true}
        formSubmitted={!!errors.ilce}
        error={!!errors.ilce}
      />
    </>
  );
} 